import { US_STATES } from '@hts/shared';
import { TextInput, SelectInput } from '../components/FormField';
import { FileUpload } from '../components/FileUpload';
import type { FormData } from '../types';

interface Props {
  data: FormData;
  onChange: (updates: Partial<FormData>) => void;
  errors?: Record<string, string>;
}

const stateOptions = US_STATES.map((s) => ({ value: s, label: s }));

export function AchStep({ data, onChange, errors = {} }: Props) {
  return (
    <div>
      <div className="step-header">
        <h2>Payment Information</h2>
        <p>Tell us how you'd like to receive your payments. This information is only used to set up your payments and is kept secure.</p>
      </div>

      {/* Payment method toggle — fieldset/legend for screen readers */}
      <fieldset
        style={{
          marginBottom: 24,
          padding: 16,
          borderRadius: 8,
          border: '2px solid #00B8EB',
          background: '#F1F4F9',
        }}
      >
        <legend style={{ padding: '0 8px', fontSize: 14, fontWeight: 600, color: '#0D2152' }}>
          How would you like to be paid?
        </legend>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
          <input
            type="radio"
            name="paymentMethod"
            value="ach"
            checked={data.paymentMethod === 'ach'}
            onChange={() => onChange({ paymentMethod: 'ach' })}
          />
          <span style={{ fontSize: 14, color: '#4A4A4A' }}>Direct deposit (ACH) — recommended</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="radio"
            name="paymentMethod"
            value="check"
            checked={data.paymentMethod === 'check'}
            onChange={() => onChange({ paymentMethod: 'check' })}
          />
          <span style={{ fontSize: 14, color: '#4A4A4A' }}>
            Paper check &mdash; mailed to your business address
          </span>
        </label>
        <p style={{ marginTop: 12, marginBottom: 0, fontSize: 13, color: '#4A4A4A' }}>
          Don&rsquo;t want to share bank details? Choose <strong>Paper check</strong> &mdash; the
          bank-account fields below will disappear and you can finish the application without them.
          You can switch to ACH later.
        </p>
      </fieldset>

      {data.paymentMethod === 'ach' ? (
        <>
          <div className="info-callout" style={{ marginBottom: 20 }}>
            <strong>Your information is safe.</strong> Your bank details are used only to generate a secure payment authorization form. They are not stored in our system after the form is created.
          </div>

          <h3 style={{ fontSize: 16, color: '#0D2152', marginBottom: 4 }}>Company Name and Address for Payments</h3>
          <p className="field-hint" style={{ marginBottom: 16 }}>Enter the name and address exactly as they appear on your bank account</p>

          <TextInput
            label="Company / Vendor Name"
            required
            value={data.achVendorName}
            error={errors.achVendorName}
            placeholder="Example: Smith Electric LLC"
            hint="The name on your bank account — this must match your bank records"
            onChange={(e) => onChange({ achVendorName: e.target.value })}
          />

          <TextInput
            label="Street Address"
            required
            value={data.achAddressLine1}
            error={errors.achAddressLine1}
            placeholder="Example: 123 Main Street"
            onChange={(e) => onChange({ achAddressLine1: e.target.value })}
          />

          <TextInput
            label="Address Line 2"
            value={data.achAddressLine2}
            placeholder="Suite, unit, etc. (if needed)"
            onChange={(e) => onChange({ achAddressLine2: e.target.value })}
          />

          <div className="form-row-3">
            <TextInput
              label="City"
              required
              value={data.achCity}
              error={errors.achCity}
              placeholder="Example: Dallas"
              onChange={(e) => onChange({ achCity: e.target.value })}
            />
            <SelectInput
              label="State"
              required
              value={data.achState}
              error={errors.achState}
              options={stateOptions}
              placeholder="Choose state..."
              onChange={(e) => onChange({ achState: e.target.value })}
            />
            <TextInput
              label="ZIP"
              required
              value={data.achZip}
              error={errors.achZip}
              placeholder="Example: 75201"
              onChange={(e) => onChange({ achZip: e.target.value })}
            />
          </div>

          <h3 style={{ fontSize: 16, color: '#0D2152', margin: '24px 0 4px' }}>Bank Account Details</h3>
          <p className="field-hint" style={{ marginBottom: 16 }}>You can find your routing number and account number on the bottom of any check from your business bank account, or by logging into your online banking.</p>

          {/* Bank check diagram */}
          <div className="check-diagram">
            <div className="check-diagram-inner">
              <div className="check-diagram-label">
                <span className="check-diagram-box routing">&#x2460; 123456789</span>
                <span className="check-diagram-desc">Routing Number (9 digits)</span>
              </div>
              <div className="check-diagram-label">
                <span className="check-diagram-box account">&#x2461; 00012345678</span>
                <span className="check-diagram-desc">Account Number</span>
              </div>
              <div className="check-diagram-label">
                <span className="check-diagram-box check-num">&#x2462; 1001</span>
                <span className="check-diagram-desc">Check Number (not needed)</span>
              </div>
            </div>
            <p className="check-diagram-caption">These numbers are printed at the bottom of your checks</p>
          </div>

          <TextInput
            label="Bank Name"
            required
            value={data.achBankName}
            error={errors.achBankName}
            placeholder="Example: Chase, Wells Fargo, Bank of America, etc."
            onChange={(e) => onChange({ achBankName: e.target.value })}
          />

          <div className="form-row">
            <TextInput
              label="Routing Number"
              required
              maxLength={9}
              value={data.achRoutingNumber}
              error={errors.achRoutingNumber}
              placeholder="9 digits — Example: 021000021"
              hint="The first 9-digit number on the bottom of your check"
              onChange={(e) => onChange({ achRoutingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) })}
            />
            <TextInput
              label="Account Number"
              required
              value={data.achAccountNumber}
              error={errors.achAccountNumber}
              placeholder="Example: 00012345678"
              hint="The second number on the bottom of your check (usually 10-12 digits)"
              onChange={(e) => onChange({ achAccountNumber: e.target.value.replace(/\D/g, '') })}
            />
          </div>

          <SelectInput
            label="Account Type"
            required
            value={data.achAccountType}
            options={[
              { value: 'checking', label: 'Checking' },
              { value: 'savings', label: 'Savings' },
            ]}
            hint="Most business accounts are checking accounts"
            onChange={(e) => onChange({ achAccountType: e.target.value as 'checking' | 'savings' })}
          />

          <FileUpload
            label="Upload a Cancelled Check or Bank Verification Letter"
            required
            accept=".pdf,.jpg,.jpeg,.png"
            file={data.bankVerifyFile}
            onChange={(bankVerifyFile) => onChange({ bankVerifyFile })}
            error={errors.bankVerifyFile}
            hint="This helps us confirm your bank details are correct. You can upload a photo of a voided check, a bank statement header, or a letter from your bank."
          />
        </>
      ) : (
        <div style={{ padding: 12, borderRadius: 6, background: '#F1F4F9', color: '#0D2152', fontSize: 14, border: '1px solid #00B8EB' }}>
          We'll mail your checks to the address you provided in Step 1.
        </div>
      )}
    </div>
  );
}
